import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { projectsAPI } from '../services/api'
import { trackWizardView, trackWizardStart } from '../utils/adsConversion'
import { CheckCircle, Loader2, Mail, ArrowRight } from 'lucide-react'

// Alles-in-één funnel voor nieuwe bedrijven: account + opdracht in één formulier.
// Stappen: form → e-mailcode bevestigen → opdracht wordt automatisch geplaatst → /creators.
// Bestaande bedrijven horen hier niet: die gebruiken /projects/create (link bovenaan).
const OpdrachtStartPage = () => {
  const navigate = useNavigate()
  const { register, verifyLogin, resendVerificationCode, isAuthenticated } = useAuth()

  const [step, setStep] = useState('form') // form | verify | creating | done
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [code, setCode] = useState('')
  const [resent, setResent] = useState(false)

  const [form, setForm] = useState({
    companyName: '', contactPerson: '', kvkNummer: '', email: '',
    password: '', passwordConfirm: '',
    title: '', description: '', contentType: 'video', budget: '', deadline: '',
    agreeTerms: false
  })

  useEffect(() => {
    document.title = 'Plaats gratis een opdracht | UGC4you'
    trackWizardView() // Meta soft conversion: wizard geladen
  }, [])

  // Ingelogde gebruikers hebben al een account → normale opdracht-flow
  useEffect(() => {
    if (isAuthenticated && step === 'form') navigate('/projects/create')
  }, [isAuthenticated])

  const set = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    if (value) trackWizardStart() // Meta soft conversion: eerste veld ingevuld (dedupe per sessie)
    setForm(f => ({ ...f, [field]: value }))
  }

  const validate = () => {
    if (!form.companyName.trim()) return 'Vul je bedrijfsnaam in'
    if (!form.contactPerson.trim()) return 'Vul een contactpersoon in'
    if (!/^\d{8}$/.test(form.kvkNummer.trim())) return 'KvK-nummer moet uit 8 cijfers bestaan'
    if (!/\S+@\S+\.\S+/.test(form.email)) return 'Vul een geldig e-mailadres in'
    if (form.password.length < 6) return 'Wachtwoord moet minimaal 6 tekens zijn'
    if (form.password !== form.passwordConfirm) return 'Wachtwoorden komen niet overeen'
    if (form.title.trim().length < 5) return 'Titel moet minimaal 5 tekens zijn'
    if (form.description.trim().length < 50) return `Omschrijving moet minimaal 50 tekens zijn (nu ${form.description.trim().length})`
    if (!form.budget || parseFloat(form.budget) < 25) return 'Budget moet minimaal €25 zijn'
    if (!form.deadline || new Date(form.deadline) <= new Date()) return 'Kies een deadline in de toekomst'
    if (!form.agreeTerms) return 'Ga akkoord met de voorwaarden om verder te gaan'
    return null
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const msg = validate()
    if (msg) { setError(msg); return }
    setError(''); setSubmitting(true)
    try {
      await register({
        role: 'bedrijf',
        email: form.email.trim(),
        password: form.password,
        companyName: form.companyName.trim(),
        contactPerson: form.contactPerson.trim(),
        kvkNummer: form.kvkNummer.trim(),
        agreeTerms: true
      })
      setStep('verify')
    } catch (err) {
      const m = err.response?.data?.message || err.response?.data?.messages?.[0] || 'Registratie mislukt, probeer het opnieuw'
      setError(m)
    } finally {
      setSubmitting(false)
    }
  }

  const createOpdracht = async () => {
    setStep('creating')
    try {
      await projectsAPI.createProject({
        title: form.title.trim(),
        description: form.description.trim(),
        contentType: form.contentType,
        budget: parseFloat(form.budget),
        deadline: new Date(form.deadline).toISOString()
      })
      setStep('done')
      setTimeout(() => navigate('/creators'), 2500)
    } catch (err) {
      // Account bestaat en is geverifieerd; alleen de opdracht faalde → terug naar verify-scherm met fout
      setError(err.response?.data?.message || err.response?.data?.messages?.[0] || 'Opdracht plaatsen mislukt')
      setStep('verify_failed')
    }
  }

  const handleVerify = async (e) => {
    e.preventDefault()
    if (code.trim().length < 4) { setError('Vul de code uit je e-mail in'); return }
    setError(''); setSubmitting(true)
    try {
      await verifyLogin(form.email.trim(), code.trim(), 'registration')
      await createOpdracht()
    } catch (err) {
      setError(err.response?.data?.message || 'Onjuiste code, probeer het opnieuw')
    } finally {
      setSubmitting(false)
    }
  }

  const inputCls = 'w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#3EB489]/50 focus:border-[#3EB489] outline-none text-[#0F172A] placeholder-[#94A3B8]'
  const labelCls = 'block text-sm font-semibold text-[#0F172A] mb-1.5'

  return (
    <div className="min-h-screen bg-white text-[#0F172A]">
      <header className="border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-xl md:text-2xl font-black tracking-tighter">UGC<span className="text-[#3EB489]">4you</span></Link>
          <Link to="/login" className="inline-flex items-center min-h-[44px] py-2 text-sm text-[#475569] hover:text-[#3EB489] transition"><span className="hidden sm:inline">Heb je al een account?&nbsp;</span><span className="font-semibold text-[#2A7E5F]">Log in</span></Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
        {step === 'form' && (
          <>
            <h1 className="text-2xl md:text-4xl font-black mb-3">Plaats gratis een opdracht</h1>
            <p className="text-[#475569] text-base md:text-lg mb-10">
              Binnen 2 minuten online — opdrachten ontvangen gemiddeld 10 voorstellen van creators.
              Je betaalt pas als je iemand kiest.
            </p>

            {error && <div className="mb-6 px-4 py-3 rounded-xl border border-red-200 text-red-600 text-sm">{error}</div>}

            <form onSubmit={handleSubmit} className="space-y-12">
              <section>
                <h2 className="text-lg md:text-xl font-bold mb-2">Jouw gegevens</h2>
                <p className="text-sm text-[#475569] mb-6">We houden je gegevens privé. Creators zien alleen je bedrijfsnaam bij de opdracht.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelCls}>Bedrijfsnaam</label>
                    <input className={inputCls} value={form.companyName} onChange={set('companyName')} placeholder="Jouw Bedrijf B.V." />
                  </div>
                  <div>
                    <label className={labelCls}>Contactpersoon</label>
                    <input className={inputCls} value={form.contactPerson} onChange={set('contactPerson')} placeholder="Jan Jansen" />
                  </div>
                  <div>
                    <label className={labelCls}>KvK-nummer</label>
                    <input className={inputCls} value={form.kvkNummer} onChange={set('kvkNummer')} placeholder="12345678" inputMode="numeric" maxLength={8} />
                  </div>
                  <div>
                    <label className={labelCls}>E-mail</label>
                    <input className={inputCls} type="email" value={form.email} onChange={set('email')} placeholder="jan@bedrijf.nl" />
                  </div>
                  <div>
                    <label className={labelCls}>Wachtwoord</label>
                    <input className={inputCls} type="password" value={form.password} onChange={set('password')} placeholder="Minimaal 6 tekens" />
                  </div>
                  <div>
                    <label className={labelCls}>Wachtwoord bevestigen</label>
                    <input className={inputCls} type="password" value={form.passwordConfirm} onChange={set('passwordConfirm')} placeholder="Herhaal wachtwoord" />
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-lg md:text-xl font-bold mb-6">Jouw opdracht</h2>
                <div className="space-y-5">
                  <div>
                    <label className={labelCls}>Titel</label>
                    <input className={inputCls} value={form.title} onChange={set('title')} placeholder="Bijv. UGC-video's voor onze nieuwe skincarelijn" maxLength={100} />
                  </div>
                  <div>
                    <label className={labelCls}>Omschrijving</label>
                    <textarea className={`${inputCls} min-h-[140px] resize-y`} value={form.description} onChange={set('description')} maxLength={2000}
                      placeholder="Vertel wat je zoekt: wat voor content, voor welk product of merk, welke stijl of doelgroep. Hoe concreter, hoe betere voorstellen je krijgt." />
                    <p className="text-xs text-[#94A3B8] mt-1.5">{form.description.trim().length}/50 tekens minimaal</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                    <div>
                      <label className={labelCls}>Type content</label>
                      <select className={inputCls} value={form.contentType} onChange={set('contentType')}>
                        <option value="video">Video</option>
                        <option value="foto">Foto</option>
                        <option value="beide">Video &amp; foto</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Budget (excl. btw)</label>
                      <input className={inputCls} type="number" min="25" step="5" value={form.budget} onChange={set('budget')} placeholder="€ 250" />
                    </div>
                    <div>
                      <label className={labelCls}>Deadline</label>
                      <input className={inputCls} type="date" value={form.deadline} onChange={set('deadline')} min={new Date(Date.now() + 864e5).toISOString().slice(0,10)} />
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <label className="flex items-start gap-3 py-1 cursor-pointer">
                  <input type="checkbox" checked={form.agreeTerms} onChange={set('agreeTerms')} className="mt-0.5 w-5 h-5 flex-shrink-0 rounded border-gray-300 text-[#3EB489] focus:ring-[#3EB489]" />
                  <span className="text-sm text-[#475569]">
                    Ik ga akkoord met de <a href="/algemene-voorwaarden" target="_blank" rel="noopener noreferrer" className="text-[#3EB489] hover:underline">Algemene Voorwaarden</a> en het <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#3EB489] hover:underline">Privacy Statement</a>
                  </span>
                </label>

                <button type="submit" disabled={submitting}
                  className="mt-8 w-full sm:w-auto px-8 py-4 bg-[#3EB489] hover:bg-[#359E78] disabled:opacity-60 rounded-xl font-bold text-white text-base inline-flex items-center justify-center gap-2 transition">
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
                  Plaats gratis mijn opdracht
                </button>
                <p className="text-xs text-[#94A3B8] mt-3">Gratis en vrijblijvend — je betaalt pas als je een voorstel accepteert.</p>
              </section>
            </form>
          </>
        )}

        {(step === 'verify' || step === 'verify_failed') && (
          <div className="max-w-md mx-auto text-center py-10">
            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl border border-gray-200 flex items-center justify-center">
              <Mail className="w-8 h-8 text-[#3EB489]" />
            </div>
            <h1 className="text-2xl md:text-3xl font-black mb-3">Bevestig je e-mailadres</h1>
            <p className="text-[#475569] mb-8">We hebben een code gestuurd naar <strong className="text-[#0F172A]">{form.email}</strong>. Vul die hieronder in — daarna staat je opdracht direct online.</p>

            {error && <div className="mb-6 px-4 py-3 rounded-xl border border-red-200 text-red-600 text-sm">{error}</div>}

            {step === 'verify_failed' ? (
              <button onClick={createOpdracht} className="w-full px-8 py-4 bg-[#3EB489] hover:bg-[#359E78] rounded-xl font-bold text-white transition">
                Opnieuw proberen te plaatsen
              </button>
            ) : (
              <form onSubmit={handleVerify}>
                <input
                  className="w-full px-4 py-4 text-center text-2xl tracking-[0.4em] font-bold bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#3EB489]/50 focus:border-[#3EB489] outline-none"
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="000000" inputMode="numeric" maxLength={6} autoFocus
                />
                <button type="submit" disabled={submitting}
                  className="mt-5 w-full px-8 py-4 bg-[#3EB489] hover:bg-[#359E78] disabled:opacity-60 rounded-xl font-bold text-white transition inline-flex items-center justify-center gap-2">
                  {submitting && <Loader2 className="w-5 h-5 animate-spin" />}
                  Bevestigen &amp; opdracht plaatsen
                </button>
              </form>
            )}

            <button
              onClick={async () => { try { await resendVerificationCode(form.email, 'registration'); setResent(true) } catch { setError('Code opnieuw sturen mislukt') } }}
              className="mt-5 text-sm text-[#475569] hover:text-[#3EB489] transition"
            >
              {resent ? 'Code opnieuw verstuurd ✓' : 'Geen code ontvangen? Stuur opnieuw'}
            </button>
          </div>
        )}

        {step === 'creating' && (
          <div className="max-w-md mx-auto text-center py-24">
            <Loader2 className="w-10 h-10 mx-auto mb-6 animate-spin text-[#3EB489]" />
            <h1 className="text-2xl font-black mb-2">Je opdracht wordt geplaatst…</h1>
            <p className="text-[#475569]">Een moment geduld.</p>
          </div>
        )}

        {step === 'done' && (
          <div className="max-w-md mx-auto text-center py-24">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full border border-emerald-200 flex items-center justify-center">
              <CheckCircle className="w-9 h-9 text-[#3EB489]" />
            </div>
            <h1 className="text-2xl md:text-3xl font-black mb-3">Je opdracht staat online! 🎉</h1>
            <p className="text-[#475569]">Creators kunnen nu reageren. We sturen je door naar de creators-pagina…</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default OpdrachtStartPage
